import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, FileCheck, Server, Users, Wifi, Check, ArrowRight, MapPin, Smartphone } from "lucide-react";
import { getCompliancePlans } from "../../lib/pricing-new.js";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

// Home health is the sharpest fit in healthcare: unlike a clinic, where staff sit
// inside one building on one controlled network, field clinicians open a laptop or
// tablet on whatever network the patient's house happens to have. The agency cannot
// govern that network, but it still owns the HIPAA obligation for ePHI crossing it.
const features = [
  {
    icon: Wifi,
    title: "Protection on networks you do not control",
    description: "Field staff connect through patient home Wi-Fi, cellular hotspots, and coffee shop networks. SACVPN encrypts the traffic before it touches any of them, so the quality of the local network stops being your exposure."
  },
  {
    icon: Smartphone,
    title: "Built for laptops and tablets in the field",
    description: "WireGuard reconnects fast when a device moves between cellular and Wi-Fi, so a nurse charting at the kitchen table does not lose the tunnel walking back to the car."
  },
  {
    icon: Lock,
    title: "Encryption for ePHI in transit",
    description: "ChaCha20 encryption protects electronic Protected Health Information as it moves between the field device and your systems, supporting HIPAA's transmission-security safeguard."
  },
  {
    icon: FileCheck,
    title: "Instant revocation when staff turn over",
    description: "Home health runs high turnover and per-visit contractors. Revoke a departing clinician's device access from one dashboard immediately, instead of hoping a laptop comes back."
  },
  {
    icon: Server,
    title: "Consistent egress IP for your EHR",
    description: "Route field traffic through a stable IP your agency software can allowlist, so remote access does not mean opening your systems to the whole internet."
  },
  {
    icon: Users,
    title: "Per-device visibility across the roster",
    description: "See which devices are provisioned and active across your field staff without collecting patient data of any kind."
  }
];

const useCases = [
  "Point-of-care charting in the patient's home",
  "OASIS assessment documentation from the field",
  "Electronic Visit Verification (EVV) check-in and check-out",
  "Agency EHR access (WellSky, Axxess, MatrixCare, Alora, HCHB)",
  "Wound photos and clinical images uploaded from a tablet",
  "Telehealth and remote clinician supervision visits",
  "Scheduling and route changes pushed to field staff",
  "Back-office billing and claims from remote coordinators"
];

const compliancePoints = [
  { title: "Transmission security", desc: "ePHI encrypted end to end across untrusted networks" },
  { title: "Access control", desc: "Device-level authentication before the network is reachable" },
  { title: "Integrity controls", desc: "Data protected against interception in transit" },
  { title: "Workforce turnover", desc: "Immediate per-device revocation when staff leave" }
];

const relatedGuides = [
  { to: "/industries/healthcare", label: "Healthcare VPN overview", desc: "Hospitals, clinics, and multi-site organizations." },
  { to: "/blog/hipaa-vpn-requirements-healthcare", label: "HIPAA VPN requirements", desc: "The technical safeguards and the questions to ask any vendor." },
  { to: "/blog/public-wifi-security-risks", label: "Public Wi-Fi risks", desc: "What actually happens on a network you do not control." },
  { to: "/blog/vpn-for-remote-work-security", label: "Remote work security guide", desc: "Practical controls for staff working outside the office." },
];

const faqs = [
  {
    q: "Why do home health agencies need a VPN more than a clinic does?",
    a: "A clinic has staff inside one building on one network the practice controls. A home health agency has clinicians opening laptops in patient houses, on residential Wi-Fi, hotspots, and public networks that the agency has no authority over. The HIPAA obligation for ePHI in transit does not move to the homeowner just because the nurse is standing in their living room, so the agency needs to protect that traffic itself."
  },
  {
    q: "Is SACVPN HIPAA compliant?",
    a: "No VPN by itself makes an organization HIPAA compliant, and any vendor claiming otherwise is overselling. SACVPN's WireGuard encryption supports the Security Rule's transmission-security safeguard for data in transit. Full compliance still depends on your access controls, audit logging, workforce training, and the other administrative and physical safeguards your agency runs."
  },
  {
    q: "Does SACVPN sign a Business Associate Agreement (BAA)?",
    a: "Not yet, and we would rather say so plainly than promise paperwork we cannot back up today. A BAA is actively in progress: the agreement is drafted from the HHS model provisions, and we are completing the upstream agreements with our infrastructure providers first, because a BAA is only meaningful if the whole chain is covered. If a signed BAA is a requirement for your agency, tell us and we will let you know the moment it is available."
  },
  {
    q: "Will it work where our nurses actually go, including rural areas with bad signal?",
    a: "WireGuard is a lightweight protocol that establishes and re-establishes connections quickly, which matters more than raw throughput on weak cellular. It adds minimal overhead compared to older protocols like OpenVPN. It cannot create signal where there is none, but it will not be the reason a weak connection fails."
  },
  {
    q: "What happens when a clinician leaves the agency?",
    a: "You revoke that device from the dashboard and its access ends immediately. Given how much turnover and per-visit contract staffing home health runs, this tends to matter more day to day than the encryption itself."
  },
  {
    q: "Does SACVPN see or store our patient data?",
    a: "No. SACVPN encrypts and routes traffic. We do not collect, inspect, or retain the clinical content moving through the tunnel, and we have no reason to."
  }
];

export default function HomeHealthVPN() {
  return (
    <>
      <Helmet>
        <title>VPN for Home Health Agencies | Protect Field Staff ePHI | SACVPN</title>
        <meta
          name="description"
          content="Home health agencies have nurses charting on patient home Wi-Fi. SACVPN encrypts ePHI in transit from any network, supports HIPAA transmission security, and revokes device access instantly when staff turn over."
        />
        <meta
          name="keywords"
          content="home health VPN, home health agency VPN, HIPAA VPN home health, field nurse VPN, hospice VPN, in-home care VPN, EVV security, point of care charting security, ePHI field staff"
        />
        <link rel="canonical" href="https://www.sacvpn.com/industries/home-health" />
        <meta property="og:title" content="VPN for Home Health Agencies | Protect Field Staff ePHI | SACVPN" />
        <meta property="og:description" content="Encrypt ePHI on patient home Wi-Fi and cellular hotspots. Built for home health, hospice, and in-home care field staff." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Home Health Agency VPN Service",
            "name": "SACVPN for Home Health Agencies",
            "description": "WireGuard VPN service for home health, hospice, and in-home care agencies whose clinicians handle ePHI on networks the agency does not control.",
            "provider": {
              "@type": "Organization",
              "name": "SACVPN"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Home Health and Hospice Agencies"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a }
            }))
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_78%_20%,rgba(14,165,233,0.14),transparent_60%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm text-brand-300 mb-6">
              <MapPin className="w-4 h-4" />
              For home health, hospice, and in-home care
            </motion.div>

            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
              Your nurses chart on Wi-Fi you will never control
            </motion.h1>

            <motion.p variants={fadeUp} className="text-lg text-gray-300 leading-relaxed mb-8">
              A clinic keeps its staff inside one building on one network. A home health agency sends
              clinicians into patient houses, where they open a laptop on whatever router happens to be
              there. The patient's network is not yours to secure, but the ePHI crossing it is still your
              obligation. SACVPN encrypts that traffic before it reaches the local network, so the state of
              someone's home Wi-Fi stops being your exposure.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-6 py-3 font-semibold text-white hover:bg-brand-400 transition-colors"
              >
                See pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-6 py-3 font-semibold text-white hover:bg-white/5 transition-colors"
              >
                Talk to us about your agency
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* HIPAA Safeguards Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Where a VPN fits in your HIPAA obligations
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 max-w-3xl mb-12">
              A VPN is one control, not a compliance program. These are the specific safeguards
              encrypted remote access supports for an agency with staff in the field.
            </motion.p>

            <motion.div variants={stagger} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {compliancePoints.map((point) => (
                <motion.div
                  key={point.title}
                  variants={fadeUp}
                  className="rounded-2xl border border-white/10 bg-white/5 p-6"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-5 h-5 text-brand-400 shrink-0" />
                    <h3 className="font-semibold text-white">{point.title}</h3>
                  </div>
                  <p className="text-sm text-gray-400">{point.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-12">
              Built for the way field staff actually work
            </motion.h2>

            <motion.div variants={stagger} className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    variants={fadeUp}
                    className="rounded-2xl border border-white/10 bg-dark-900/60 p-6 hover:border-brand-500/30 transition-colors"
                  >
                    <div className="w-11 h-11 rounded-xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-brand-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{feature.description}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="grid gap-12 lg:grid-cols-2 lg:items-start"
          >
            <div>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-4">
                What runs over the tunnel
              </motion.h2>
              <motion.p variants={fadeUp} className="text-gray-400 leading-relaxed mb-6">
                Everything a clinician touches between the driveway and the next visit. If it moves
                patient information across a network your agency does not own, it belongs inside an
                encrypted tunnel.
              </motion.p>
              <motion.p variants={fadeUp} className="text-gray-400 leading-relaxed">
                That includes the systems people forget about: EVV check-ins, wound photos taken on a
                tablet, and schedule changes pushed mid-route.
              </motion.p>
            </div>

            <motion.ul variants={stagger} className="space-y-3">
              {useCases.map((useCase) => (
                <motion.li
                  key={useCase}
                  variants={fadeUp}
                  className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                >
                  <Check className="w-5 h-5 text-brand-400 shrink-0 mt-0.5" />
                  <span className="text-gray-200">{useCase}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>
      </section>

      {/* Related Guides Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-12">
              Related reading
            </motion.h2>

            <motion.div variants={stagger} className="grid gap-6 sm:grid-cols-2">
              {relatedGuides.map((guide) => (
                <motion.div key={guide.to} variants={fadeUp}>
                  <Link
                    to={guide.to}
                    className="group block rounded-2xl border border-white/10 bg-dark-900/60 p-6 hover:border-brand-500/30 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <h3 className="text-lg font-semibold text-white group-hover:text-brand-300 transition-colors">
                        {guide.label}
                      </h3>
                      <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-brand-400 group-hover:translate-x-1 transition-all shrink-0" />
                    </div>
                    <p className="text-sm text-gray-400">{guide.desc}</p>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Pricing for agencies
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 mb-12 max-w-2xl">
              Priced per agency, not per seat you have to count every month. No setup fee and no
              contract. Start on a few devices and expand once you have seen it work on real routes.
            </motion.p>

            <motion.div variants={stagger} className="grid gap-6 md:grid-cols-2">
              {getCompliancePlans().map((plan) => (
                <motion.div
                  key={plan.code}
                  variants={fadeUp}
                  className="rounded-2xl border border-white/10 bg-white/5 p-8 hover:border-brand-500/30 transition-colors"
                >
                  <h3 className="text-xl font-semibold text-white mb-1">{plan.name}</h3>
                  <p className="text-sm text-gray-400 mb-5">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">${plan.monthlyPrice}</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <ul className="space-y-2 mb-8">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-300">
                        <Check className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-5 py-3 font-semibold text-white hover:bg-brand-400 transition-colors"
                  >
                    Talk to us
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            <motion.p variants={fadeUp} className="text-sm text-gray-500 mt-6">
              Running more than 50 field devices, or need a dedicated node? Get in touch and we will
              size it with you.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-12">
              Questions agencies ask
            </motion.h2>

            <motion.div variants={stagger} className="space-y-4">
              {faqs.map((faq) => (
                <motion.div
                  key={faq.q}
                  variants={fadeUp}
                  className="rounded-2xl border border-white/10 bg-white/5 p-6"
                >
                  <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{faq.a}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Protect the traffic before it leaves the house
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Set it up on a few devices first and watch how it behaves on real routes before you roll it
              out to the whole roster.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-6 py-3 font-semibold text-white hover:bg-brand-400 transition-colors"
              >
                See pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-6 py-3 font-semibold text-white hover:bg-white/5 transition-colors"
              >
                Contact us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
